import React, { useState } from "react";
import { Link } from "react-router-dom";
import PageTopSlider from "../components/PageTopSlider";
import GalleryViewer from "../components/GalleryViewer";
import ReviewsSlider from "../components/ReviewsSlider";
import ContactForm from "../components/ContactForm";
import SEO from "../components/SEO";

export default function Energimaerkning() {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: "Hvad betyder energimærkning?",
      a: "Energimærkning er en vurdering af en bygnings energiforbrug og effektivitet. Mærket viser, hvor meget energi ejendommen bruger, og hvor der er mulighed for forbedringer. Skalaen går fra A2020, som er det mest energieffektive niveau, til G, som er det mindst effektive.",
    },
    {
      q: "Hvad koster det at få lavet energimærkning hos ET Danmark ApS?",
      a: "Prisen for en energimærkning starter fra 4.188 kr. inkl. moms. Prisen dækker gennemgangen af ejendommen samt udarbejdelsen af den tilhørende rapport med anbefalinger til energioptimering.",
    },
    {
      q: "Hvordan beregnes energimærkning?",
      a: "Energimærkningen beregnes af en certificeret energikonsulent, der foretager en grundig gennemgang af bygningen. Konsulenten vurderer blandt andet isolering, vinduer, døre, ventilation og varmeanlæg. På baggrund af gennemgangen og beregninger af energiforbruget tildeles ejendommen en karakter på skalaen fra A2020 til G.",
    },
    {
      q: "Hvad er bedst energimærke A eller F?",
      a: "Et energimærke A er langt bedre end et energimærke F. A-mærkede bygninger har lavt energiforbrug og høj effektivitet, mens F-mærkede bygninger bruger markant mere energi. Et godt energimærke betyder lavere udgifter, højere komfort og typisk også en højere ejendomsværdi.",
    },
    {
      q: "Hvor længe holder et energimærke?",
      a: "Et energimærke er gyldigt i 10 år fra udstedelsesdatoen. Efter denne periode skal der udarbejdes en ny energimærkning for at sikre, at vurderingen stadig afspejler bygningens aktuelle energistandard og eventuelle forbedringer.",
    },
    {
      q: "Hvem må lave energimærket?",
      a: "Kun certificerede energikonsulenter må udarbejde energimærkninger. Energikonsulenter har den nødvendige uddannelse og autorisation til at vurdere en bygnings energiforbrug og udarbejde den officielle rapport.",
    },
    {
      q: "Hvordan får man et energimærke?",
      a: "For at få et energimærke skal man kontakte en certificeret energikonsulent, som gennemgår ejendommen og beregner energiforbruget. Efter gennemgangen modtager ejeren en rapport med både energimærket og konkrete forslag til forbedringer, der kan øge energieffektiviteten og reducere energiomkostningerne.",
    },
    {
      q: "Hvornår skal man have et energimærke?",
      a: "Energimærkning er lovpligtig ved salg og udlejning af ejendomme. Formålet er at give købere eller lejere et klart billede af bygningens energiforbrug, før de træffer beslutning. Derudover kan det være relevant at få en ny mærkning efter større renoveringer, hvor energieffektiviteten er forbedret.",
    },
    {
      q: "Hvad kigger man på i et energimærke?",
      a: "Når en bygning energimærkes, gennemgår en energikonsulent blandt andet isolering, vinduer, varmeanlæg, ventilation og bygningens generelle tilstand. Disse faktorer danner grundlag for den samlede vurdering af ejendommens energiforbrug og potentiale for optimering.",
    },
    {
      q: "Hvordan forbedrer man sit energimærke?",
      a: "Der findes flere måder at forbedre et energimærke på. Typiske tiltag inkluderer bedre isolering, udskiftning af vinduer og døre, installation af energieffektive varmeanlæg og optimeret ventilation. Disse forbedringer kan reducere varmeforbruget, øge komforten og samtidig gøre ejendommen mere attraktiv ved salg eller udlejning.",
    },
  ];

  const primaryCities = [
    { name: "Horsens", slug: "horsens" },
    { name: "Vejle", slug: "vejle" },
    { name: "Aarhus", slug: "aarhus" },
    { name: "Skanderborg", slug: "skanderborg" },
    { name: "Juelsminde", slug: "juelsminde" },
    { name: "Aalborg", slug: "aalborg" },
    { name: "Herning", slug: "herning" },
    { name: "Randers", slug: "randers" },
    { name: "Fredericia", slug: "fredericia" },
    { name: "Kolding", slug: "kolding" },
    { name: "Odense", slug: "odense" },
    { name: "Svendborg", slug: "svendborg" },
  ];

  return (
    <div className="bg-[#FAF9F6] text-stone-800">
      <SEO
        title="Energimærkning i hele Jylland og Fyn | Energioptimering | Få et tilbud"
        description="Vi har stor erfaring med energimærkning og energioptimering af huse og bygninger. Få hjælp til at identificere de bedste muligheder for dit hus."
        canonical="https://etdanmark.com/energimaerkning"
        ogImage="https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07467.jpg"
      />
      {/* 1. TOP SLIDER HERO */}
      <PageTopSlider
        title="Energimærkning"
        subtitle="Er du klar over, hvordan en god energimærkning kan øge værdien af din ejendom og samtidig reducere dine energiomkostninger? Få professionel rådgivning og hurtig levering i hele Jylland og Fyn."
        badge="Certificeret energikonsulent i hele Jylland og Fyn"
        breadcrumbs={[
          { label: "Forside", to: "/" },
          { label: "Ydelser", to: "/#ydelser" },
          { label: "Energimærkning" },
        ]}
        priceBadge="Fra 4.188 kr. inkl. moms"
        actions={
          <>
            <Link
              to="/prisberegner"
              className="px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-md transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-calculator text-xs" aria-hidden="true"></i>
              <span>Beregn din pris</span>
            </Link>
            <a
              href="tel:+4542499595"
              className="px-6 py-3 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white font-bold text-sm backdrop-blur-xs transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-phone text-emerald-300 text-xs" aria-hidden="true"></i>
              <span>Ring 42 49 95 95</span>
            </a>
          </>
        }
        image={{
          url: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07506.jpg",
          alt: "Energimærkning af bolig og ejendom",
        }}
      />

      {/* RÅDGIVNING & INTRODUKTION */}
      <section className="py-14 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                Værdien af en korrekt energimærkning
              </h2>

              <div className="space-y-3 text-stone-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Mange husejere og bygningsansvarlige er stadig i tvivl om, hvordan energimærkning af bygninger fungerer, og hvilken betydning det har for deres økonomi og miljø.
                </p>
                <p>
                  Har du behov for rådgivning omkring hvordan du bedst kan optimere din bolig, kan vi også være behjælpelige. Vi har hjulpet kunder i hele Jylland og Fyn og står også klar til at hjælpe dig.
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-[#11463A] text-white flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm">
                <div>
                  <span className="text-xs uppercase text-emerald-300 font-semibold block">Prisstruktur:</span>
                  <span className="text-2xl font-bold text-white">Fra 4.188 kr. inkl. moms</span>
                </div>
                <div className="flex gap-2">
                  <a
                    href="#kontakt"
                    className="px-5 py-2.5 rounded-full bg-white text-stone-900 font-bold text-xs hover:bg-stone-100 transition-colors"
                  >
                    Kontakt os
                  </a>
                  <Link
                    to="/prisberegner"
                    className="px-5 py-2.5 rounded-full bg-emerald-600 text-white font-bold text-xs hover:bg-emerald-500 transition-colors"
                  >
                    Beregn pris
                  </Link>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-stone-100 bg-stone-100 aspect-16/11">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07467.jpg"
                  alt="Energikonsulent rådgivning og bygningsgennemgang"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESSEN FOR ENERGIMÆRKNING */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-stone-100/60 via-stone-50/80 to-white border-b border-stone-200/70 relative overflow-hidden">
        {/* Subtil baggrundsglød */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[300px] bg-emerald-500/5 rounded-full blur-3xl pointer-events-none -z-0" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-14 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-stone-900 tracking-tight">
              Sådan foregår energimærkningen
            </h2>
            <p className="mt-3 text-stone-600 text-sm sm:text-base leading-relaxed">
              Vi gør forløbet trygt, hurtigt og ubesværet. Du skal blot bestille – så klarer vi opmåling, beregninger og den officielle indberetning:
            </p>
          </div>

          {/* Process Timeline / Cards */}
          <div className="relative">
            {/* Forbindelseslinje på store skærme */}
            <div className="hidden lg:block absolute top-[52px] left-12 right-12 h-0.5 bg-gradient-to-r from-emerald-200 via-emerald-400 to-emerald-600/60 -z-0" />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 sm:gap-6">
              {/* Trin 1 */}
              <div className="group bg-white rounded-2xl p-5 sm:p-6 border border-stone-200/90 shadow-sm hover:shadow-xl hover:border-emerald-500/60 hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between relative">
                {/* Connector pil til næste trin (desktop) */}
                <div className="hidden lg:flex absolute -right-3.5 top-9 w-7 h-7 rounded-full bg-white border border-stone-200 text-stone-400 items-center justify-center text-[10px] z-10 shadow-xs group-hover:border-emerald-400 group-hover:text-emerald-600 transition-colors">
                  <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#165B4C] to-[#0e382e] text-white flex items-center justify-center font-black text-sm shadow-md shadow-emerald-950/15 group-hover:scale-105 transition-transform">
                      01
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-stone-500 bg-stone-100/90 px-2.5 py-1 rounded-md border border-stone-200/60">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                      <span>Nemt & digitalt</span>
                    </div>
                  </div>

                  <h3 className="font-extrabold text-stone-900 text-base sm:text-lg mb-2 group-hover:text-[#165B4C] transition-colors">
                    Kunden bestiller
                  </h3>
                  <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                    Bestil online via vores prisberegner, kontaktformular eller ved et hurtigt opkald.
                  </p>
                </div>

                <div className="mt-5 pt-3.5 border-t border-stone-100 flex items-center gap-1.5 text-[11px] font-semibold text-[#165B4C] bg-emerald-50/50 -mx-1 px-2 py-1.5 rounded-lg">
                  <i className="fa-regular fa-clock text-emerald-600 text-xs" aria-hidden="true"></i>
                  <span>Tager under 1 minut</span>
                </div>
              </div>

              {/* Trin 2 */}
              <div className="group bg-white rounded-2xl p-5 sm:p-6 border border-stone-200/90 shadow-sm hover:shadow-xl hover:border-emerald-500/60 hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between relative">
                {/* Connector pil til næste trin (desktop) */}
                <div className="hidden lg:flex absolute -right-3.5 top-9 w-7 h-7 rounded-full bg-white border border-stone-200 text-stone-400 items-center justify-center text-[10px] z-10 shadow-xs group-hover:border-emerald-400 group-hover:text-emerald-600 transition-colors">
                  <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#165B4C] to-[#0e382e] text-white flex items-center justify-center font-black text-sm shadow-md shadow-emerald-950/15 group-hover:scale-105 transition-transform">
                      02
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-stone-500 bg-stone-100/90 px-2.5 py-1 rounded-md border border-stone-200/60">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                      <span>Forberedelse</span>
                    </div>
                  </div>

                  <h3 className="font-extrabold text-stone-900 text-base sm:text-lg mb-2 group-hover:text-[#165B4C] transition-colors">
                    Vi indhenter data
                  </h3>
                  <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                    Vi indhenter relevante BBR-data, historiske tegninger og offentlige bygningsoplysninger.
                  </p>
                </div>

                <div className="mt-5 pt-3.5 border-t border-stone-100 flex items-center gap-1.5 text-[11px] font-semibold text-[#165B4C] bg-emerald-50/50 -mx-1 px-2 py-1.5 rounded-lg">
                  <i className="fa-solid fa-file-lines text-emerald-600 text-xs" aria-hidden="true"></i>
                  <span>Vi klarer papirarbejdet</span>
                </div>
              </div>

              {/* Trin 3 - Fremhævet kerne-trin */}
              <div className="group bg-white rounded-2xl p-5 sm:p-6 border-2 border-emerald-500/50 shadow-md hover:shadow-xl hover:border-emerald-600 hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between relative bg-gradient-to-b from-white to-emerald-50/30">
                {/* Lille top-badge */}
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-[#165B4C] text-white text-[10px] font-extrabold uppercase tracking-wider shadow-xs whitespace-nowrap">
                  Besigtigelse
                </div>

                {/* Connector pil til næste trin (desktop) */}
                <div className="hidden lg:flex absolute -right-3.5 top-9 w-7 h-7 rounded-full bg-white border border-stone-200 text-stone-400 items-center justify-center text-[10px] z-10 shadow-xs group-hover:border-emerald-400 group-hover:text-emerald-600 transition-colors">
                  <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-600 to-[#11463A] text-white flex items-center justify-center font-black text-sm shadow-md shadow-emerald-950/20 group-hover:scale-105 transition-transform">
                      03
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800 bg-emerald-100/70 px-2.5 py-1 rounded-md border border-emerald-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                      <span>På adressen</span>
                    </div>
                  </div>

                  <h3 className="font-extrabold text-stone-900 text-base sm:text-lg mb-2 group-hover:text-[#165B4C] transition-colors">
                    Boligen gennemgås
                  </h3>
                  <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                    Certificeret energikonsulent gennemgår ejendommen grundigt, måler op og tjekker isolering og varme.
                  </p>
                </div>

                <div className="mt-5 pt-3.5 border-t border-emerald-100 flex items-center gap-1.5 text-[11px] font-semibold text-emerald-800 bg-emerald-100/60 -mx-1 px-2 py-1.5 rounded-lg">
                  <i className="fa-solid fa-user-check text-emerald-600 text-xs" aria-hidden="true"></i>
                  <span>Uvildig konsulent</span>
                </div>
              </div>

              {/* Trin 4 */}
              <div className="group bg-white rounded-2xl p-5 sm:p-6 border border-stone-200/90 shadow-sm hover:shadow-xl hover:border-emerald-500/60 hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between relative">
                {/* Connector pil til næste trin (desktop) */}
                <div className="hidden lg:flex absolute -right-3.5 top-9 w-7 h-7 rounded-full bg-white border border-stone-200 text-stone-400 items-center justify-center text-[10px] z-10 shadow-xs group-hover:border-emerald-400 group-hover:text-emerald-600 transition-colors">
                  <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#165B4C] to-[#0e382e] text-white flex items-center justify-center font-black text-sm shadow-md shadow-emerald-950/15 group-hover:scale-105 transition-transform">
                      04
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-stone-500 bg-stone-100/90 px-2.5 py-1 rounded-md border border-stone-200/60">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                      <span>Beregning</span>
                    </div>
                  </div>

                  <h3 className="font-extrabold text-stone-900 text-base sm:text-lg mb-2 group-hover:text-[#165B4C] transition-colors">
                    Beregnes & indberettes
                  </h3>
                  <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                    Energimærket udregnes og indberettes direkte til Energistyrelsens officielle register.
                  </p>
                </div>

                <div className="mt-5 pt-3.5 border-t border-stone-100 flex items-center gap-1.5 text-[11px] font-semibold text-[#165B4C] bg-emerald-50/50 -mx-1 px-2 py-1.5 rounded-lg">
                  <i className="fa-solid fa-shield-check text-emerald-600 text-xs" aria-hidden="true"></i>
                  <span>Officiel indberetning</span>
                </div>
              </div>

              {/* Trin 5 */}
              <div className="group bg-white rounded-2xl p-5 sm:p-6 border border-stone-200/90 shadow-sm hover:shadow-xl hover:border-emerald-500/60 hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between relative">
                <div>
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#165B4C] to-[#0e382e] text-white flex items-center justify-center font-black text-sm shadow-md shadow-emerald-950/15 group-hover:scale-105 transition-transform">
                      05
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-stone-500 bg-stone-100/90 px-2.5 py-1 rounded-md border border-stone-200/60">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                      <span>Afslutning</span>
                    </div>
                  </div>

                  <h3 className="font-extrabold text-stone-900 text-base sm:text-lg mb-2 group-hover:text-[#165B4C] transition-colors">
                    Officiel rapport
                  </h3>
                  <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                    Du modtager den godkendte rapport med energimærke og klare forslag til rentable energibesparelser.
                  </p>
                </div>

                <div className="mt-5 pt-3.5 border-t border-stone-100 flex items-center gap-1.5 text-[11px] font-semibold text-[#165B4C] bg-emerald-50/50 -mx-1 px-2 py-1.5 rounded-lg">
                  <i className="fa-solid fa-award text-emerald-600 text-xs" aria-hidden="true"></i>
                  <span>Gyldig i op til 10 år</span>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Banner med dybde og høj konvertering */}
          <div className="mt-12 sm:mt-14 relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#0c3329] via-[#165B4C] to-[#0f3d31] p-6 sm:p-10 text-white shadow-xl border border-emerald-500/20">
            {/* Bagggrunds-dekorativ glød */}
            <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-400/10 rounded-full blur-2xl pointer-events-none" />

            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-6 sm:gap-8">
              <div className="space-y-2 text-center lg:text-left max-w-2xl">
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-white tracking-tight">
                  Hvad koster energimærkning af din bolig?
                </h3>
                <p className="text-emerald-100/90 text-xs sm:text-sm leading-relaxed">
                  Beregn din pris på under 1 minut eller kontakt os for et uforpligtende tilbud.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto shrink-0">
                <Link
                  to="/prisberegner"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-full bg-emerald-500 hover:bg-emerald-400 text-stone-950 font-extrabold text-sm sm:text-base shadow-lg shadow-emerald-950/30 hover:shadow-emerald-500/20 hover:-translate-y-0.5 transition-all group"
                >
                  <i className="fa-solid fa-calculator text-sm" aria-hidden="true"></i>
                  <span>Beregn din pris her</span>
                  <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                </Link>
                <a
                  href="tel:+4542499595"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-xs sm:text-sm border border-white/15 transition-colors"
                >
                  <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
                  <span>Ring 42 49 95 95</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. HVAD ER ENERGIMÆRKNING */}
      <section className="py-16 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                Hvad er energimærkning?
              </h2>
              <div className="space-y-3 text-stone-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Energimærkning er en vurdering af et bygnings energiforbrug. Energimærket giver en indikation af, hvor energieffektiv din bygning er, og hvad der kan forbedres for at reducere energiforbruget. Energimærket spænder fra A2020, som er det bedste, til G, som er det dårligste.
                </p>
                <p>
                  Dette mærke er lovpligtigt ved salg og udlejning af ejendomme, og det giver potentielle købere eller lejere en idé om bygningens energiforbrug.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5 bg-stone-50 border border-stone-200/90 p-6 rounded-3xl space-y-3">
              <h3 className="font-bold text-stone-900 text-sm uppercase tracking-wider mb-2">
                Energiskalaen: A2020 til G
              </h3>
              <div className="space-y-1.5 text-xs font-bold text-white">
                <div className="p-2 rounded bg-emerald-700 flex justify-between">
                  <span>A2020 &bull; Højeste energieffektivitet</span>
                  <span>Meget lavt forbrug</span>
                </div>
                <div className="p-2 rounded bg-emerald-600 flex justify-between">
                  <span>A2015 &bull; A2010</span>
                  <span>Moderne standard</span>
                </div>
                <div className="p-2 rounded bg-yellow-500 flex justify-between text-stone-900">
                  <span>B &bull; C</span>
                  <span>Mellem standard</span>
                </div>
                <div className="p-2 rounded bg-orange-600 flex justify-between">
                  <span>D &bull; E</span>
                  <span>Ældre boliger</span>
                </div>
                <div className="p-2 rounded bg-red-700 flex justify-between">
                  <span>F &bull; G</span>
                  <span>Stort optimeringspotentiale</span>
                </div>
              </div>
              <p className="text-[11px] text-stone-400 pt-1">
                Rapporten giver forslag til rentable energioptimeringer, hvor det er muligt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. FORDELE VED ENERGIMÆRKNING */}
      <section className="py-16 sm:py-20 bg-stone-50 border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Fordele ved energimærkning
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-2">
              En god energimærkning af huse, kan føre til flere fordele:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-2xs">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center mb-4">
                <i className="fa-solid fa-arrow-trend-up" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-base">Øget ejendomsværdi</h3>
              <p className="text-xs text-stone-600 mt-2 leading-relaxed">
                Huse med bedre energioptimering sælges ofte hurtigere og til en højere pris.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-2xs">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center mb-4">
                <i className="fa-solid fa-coins" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-base">Reducerede energiomkostninger</h3>
              <p className="text-xs text-stone-600 mt-2 leading-relaxed">
                Ved at følge anbefalingerne fra energimærkningen, kan du reducere dine energiudgifter betydeligt.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-2xs">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center mb-4">
                <i className="fa-solid fa-leaf" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-base">Miljømæssige fordele</h3>
              <p className="text-xs text-stone-600 mt-2 leading-relaxed">
                Lavere energiforbrug betyder mindre CO2-udledning, hvilket er godt for miljøet.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-2xs">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center mb-4">
                <i className="fa-solid fa-couch" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-base">Komfort</h3>
              <p className="text-xs text-stone-600 mt-2 leading-relaxed">
                Bedre isolering og energieffektivitet øger komforten i dit hjem.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. HVORDAN FOREGÅR ENERGIMÆRKNING? */}
      <section className="py-16 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl space-y-4">
            <h2 className="text-3xl font-extrabold text-stone-900 tracking-tight">
              Hvordan foregår energimærkning?
            </h2>
            <div className="space-y-3 text-stone-600 text-sm sm:text-base leading-relaxed">
              <p>
                Processen for at energimærke bygninger, indebærer en grundig gennemgang af ejendommen af en certificeret energikonsulent. Konsulenten vurderer isolering, varmeinstallationer, vinduer, ventilation og andre energirelaterede aspekter af bygningen.
              </p>
              <p>
                Efter denne gennemgang udstedes et energimærke, der placerer bygningen på en skala fra A2020 (meget energieffektiv) til G (mindre energieffektiv). Samtidig modtager du en rapport med specifikke anbefalinger til energioptimering af huset. Disse forslag kan omfatte forbedringer som bedre isolering, udskiftning af vinduer og modernisering af varmeanlæg og ventilationssystemer.
              </p>
              <p>
                Rapporten fungerer som en vejledning til at reducere energiomkostningerne, forbedre komforten og øge ejendommens værdi ved fremtidige salg eller udlejning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. ENERGIOPTIMERING AF HUS & NY ENERGIMÆRKNING */}
      <section className="relative overflow-hidden py-16 sm:py-20 border-b border-stone-200/70">
        {/* Baggrundsbillede bag boksene */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07546.jpg"
            alt="Energioptimering af hus og bygningstegninger"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-stone-950/65 backdrop-blur-[1px]"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white/95 backdrop-blur-md p-8 rounded-3xl border border-white/60 shadow-xl space-y-4">
              <h2 className="text-2xl font-bold text-stone-900 tracking-tight">
                Energioptimering af hus
              </h2>
              <div className="space-y-3 text-xs sm:text-sm text-stone-600 leading-relaxed">
                <p>
                  Energioptimering af dit hus kan omfatte en række forskellige forbedringer, der både øger boligens komfort, reducerer energiforbruget og kan forbedre energimærket. En af de mest effektive løsninger er at efterisolere vægge, loft og gulv, da det kan mindske varmetabet betydeligt og sænke varmeudgifterne. Det kan også være en fordel at udskifte ældre vinduer og døre med moderne, energieffektive løsninger, som holder bedre på varmen og reducerer træk.
                </p>
                <p>
                  Derudover kan et nyt og mere effektivt varmeanlæg, eksempelvis en varmepumpe eller fjernvarme, bidrage til et lavere energiforbrug og en mere økonomisk opvarmning. Endelig kan installation af mekanisk ventilation med varmegenvinding forbedre indeklimaet, samtidig med at varmen i boligen udnyttes mere effektivt.
                </p>
              </div>
            </div>

            <div className="bg-white/95 backdrop-blur-md p-8 rounded-3xl border border-white/60 shadow-xl space-y-4">
              <h2 className="text-2xl font-bold text-stone-900 tracking-tight">
                Ny energimærkning – hvornår er det nødvendigt?
              </h2>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                Ny energimærkning kan være nødvendig ved flere lejligheder:
              </p>
              <div className="space-y-3 text-xs sm:text-sm text-stone-700">
                <div className="p-3 rounded-xl bg-stone-50 border border-stone-200/80">
                  <span className="font-bold text-stone-900 block mb-0.5">Salg eller udlejning :</span>
                  <span>Hvis du planlægger at sælge eller udleje din ejendom, skal du have en gyldig mærkning.</span>
                </div>
                <div className="p-3 rounded-xl bg-stone-50 border border-stone-200/80">
                  <span className="font-bold text-stone-900 block mb-0.5">Renovering :</span>
                  <span>Efter større renoveringer kan det være en god idé at få en ny energimærkning, for at afspejle forbedringerne i ejendommens energieffektivitet.</span>
                </div>
                <div className="p-3 rounded-xl bg-stone-50 border border-stone-200/80">
                  <span className="font-bold text-stone-900 block mb-0.5">Regelmæssig opdatering :</span>
                  <span>mærkningerne har en gyldighed på 10 år. Efter denne periode skal du få udarbejdet en ny energimærkning.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. HVORFOR VÆLGE OS & DÆKNING */}
      <section className="py-16 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-6 space-y-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                Hvorfor vælge os?
              </h2>
              <div className="space-y-3 text-stone-600 text-sm leading-relaxed">
                <p>
                  Ved at vælge en erfaren og certificeret energikonsulent sikrer du, at din mærkning udføres professionelt og grundigt.
                </p>
                <p>
                  Vi har stor erfaring med at vurdere både huse og bygninger, og vi kan hjælpe dig med at identificere de bedste muligheder for energioptimering af dit hus.
                </p>
                <p>
                  Vores ekspertise sikrer, at du får en pålidelig vurdering og praktiske råd til forbedringer.
                </p>
              </div>
            </div>

            <div className="lg:col-span-6 space-y-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                Energimærkning i hele Jylland og Fyn
              </h2>
              <p className="text-stone-600 text-sm leading-relaxed">
                Vi tilbyder energimærkning af huse og bygninger i hele Jylland og Fyn. Som certificerede energikonsulenter er vi klar til at hjælpe dig med at optimere energiforbruget og forbedre energimærket i din ejendom.
              </p>
              <p className="text-xs font-semibold text-stone-800">
                Her er nogle af de primære byer, hvor vi opererer:
              </p>
              <div className="flex flex-wrap gap-2">
                {primaryCities.map((city) => (
                  <Link
                    key={city.slug}
                    to={`/energimaerkning-i-${city.slug}`}
                    className="px-3 py-1.5 rounded-lg bg-stone-100 hover:bg-emerald-50 border border-stone-200 hover:border-emerald-500/50 text-xs font-semibold text-stone-800 hover:text-emerald-800 transition-all shadow-2xs inline-flex items-center gap-1"
                  >
                    <span>{city.name}</span>
                    <i className="fa-solid fa-arrow-right text-[9px] text-emerald-600 opacity-60" aria-hidden="true"></i>
                  </Link>
                ))}
              </div>
              <p className="text-xs text-stone-500 leading-relaxed pt-1">
                Udover disse byer dækker vi også andre områder som{" "}
                <Link to="/energimaerkning-i-esbjerg" className="text-emerald-700 font-semibold hover:underline">
                  Esbjerg
                </Link>
                ,{" "}
                <Link to="/energimaerkning-i-silkeborg" className="text-emerald-700 font-semibold hover:underline">
                  Silkeborg
                </Link>
                ,{" "}
                <Link to="/energimaerkning-i-viborg" className="text-emerald-700 font-semibold hover:underline">
                  Viborg
                </Link>
                , og mange flere. Vi stræber efter at tilbyde vores ekspertise i hele Jylland og Fyn for at sikre, at alle kan drage fordel af energieffektive løsninger og forbedret ejendomsværdi.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 7. CMS GALLERI */}
      <section className="py-16 bg-stone-50 border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <GalleryViewer
            gallerySlug="energimaerkning"
            title="Galleri: Energimærkning af huse"
            subtitle="Billeder fra besigtigelser, varmesystemer og bygningsgennemgange"
          />
        </div>
      </section>

      {/* PRISOVERSIGT SEKTION */}
      <section className="py-16 sm:py-20 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-[#165B4C] text-xs font-bold uppercase tracking-wider mb-3">
              <i className="fa-solid fa-tag" aria-hidden="true"></i>
              <span>Gennemskuelige priser inkl. moms</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Hvad koster en energimærkning?
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-3 leading-relaxed">
              Vores priser er faste og gennemskuelige afhængigt af boligens type og areal. Altid inklusiv moms, kørsel i Jylland & Fyn og komplet officiel rapport.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-2xl bg-[#FAF9F6] border border-stone-200 hover:border-emerald-400 transition-colors flex flex-col justify-between">
              <div>
                <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider block mb-1">Enfamiliehuse</span>
                <h3 className="text-lg font-bold text-stone-900">0–99 m²</h3>
                <p className="text-xs text-stone-500 mt-1">Mindre bolig, sommerhus eller rækkehus</p>
              </div>
              <div className="mt-6 pt-4 border-t border-stone-200">
                <span className="text-xs text-stone-500 block">Fast pris inkl. moms</span>
                <span className="text-2xl font-extrabold text-[#165B4C] block mt-0.5">4.188 kr.</span>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-[#FAF9F6] border-2 border-[#165B4C]/40 shadow-xs relative flex flex-col justify-between">
              <div className="absolute -top-3 right-4 bg-[#165B4C] text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                Mest almindelig
              </div>
              <div>
                <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider block mb-1">Enfamiliehuse</span>
                <h3 className="text-lg font-bold text-stone-900">100–199 m²</h3>
                <p className="text-xs text-stone-500 mt-1">Klassisk parcelhus eller villa</p>
              </div>
              <div className="mt-6 pt-4 border-t border-stone-200">
                <span className="text-xs text-stone-500 block">Fast pris inkl. moms</span>
                <span className="text-2xl font-extrabold text-[#165B4C] block mt-0.5">4.488 kr.</span>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-[#FAF9F6] border border-stone-200 hover:border-emerald-400 transition-colors flex flex-col justify-between">
              <div>
                <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider block mb-1">Enfamiliehuse</span>
                <h3 className="text-lg font-bold text-stone-900">200–299 m²</h3>
                <p className="text-xs text-stone-500 mt-1">Større bolig eller toplansvilla</p>
              </div>
              <div className="mt-6 pt-4 border-t border-stone-200">
                <span className="text-xs text-stone-500 block">Fast pris inkl. moms</span>
                <span className="text-2xl font-extrabold text-[#165B4C] block mt-0.5">4.888 kr.</span>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-[#FAF9F6] border border-stone-200 hover:border-emerald-400 transition-colors flex flex-col justify-between">
              <div>
                <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider block mb-1">Enfamiliehuse</span>
                <h3 className="text-lg font-bold text-stone-900">300 m² og derover</h3>
                <p className="text-xs text-stone-500 mt-1">Meget store villaer eller landejendomme</p>
              </div>
              <div className="mt-6 pt-4 border-t border-stone-200">
                <span className="text-xs text-stone-500 block">Fra-pris inkl. moms</span>
                <span className="text-2xl font-extrabold text-[#165B4C] block mt-0.5">Fra 5.500 kr.</span>
              </div>
            </div>
          </div>

          {/* Kombipakke banner */}
          <div className="mt-8 p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-[#11463A] to-[#165B4C] text-white shadow-lg flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2">
              <span className="inline-block px-3 py-1 rounded-full bg-emerald-400/20 text-emerald-200 border border-emerald-400/30 text-xs font-semibold uppercase tracking-wider">
                Kombipakke • Spar 600 kr.
              </span>
              <h3 className="text-2xl font-bold text-white tracking-tight">
                Kombiner med trykprøvning & spar 600 kr.
              </h3>
              <p className="text-stone-200 text-sm max-w-2xl leading-relaxed">
                Få udført energimærkning og trykprøvning samlet ved samme besøg og spar 600 kr. Kombipakke fra 6.888 kr. inkl. moms. Prisen afhænger af boligens størrelse.
              </p>
            </div>
            <div className="shrink-0 flex flex-col sm:flex-row items-center gap-3">
              <Link
                to="/prisberegner"
                className="w-full sm:w-auto px-6 py-3 rounded-full bg-white hover:bg-stone-100 text-[#11463A] font-bold text-xs inline-flex items-center justify-center gap-2 transition-all shadow-md"
              >
                <i className="fa-solid fa-calculator text-xs" aria-hidden="true"></i>
                <span>Beregn din pris online</span>
              </Link>
              <Link
                to="/priser"
                className="w-full sm:w-auto px-6 py-3 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white font-bold text-xs inline-flex items-center justify-center gap-2 transition-all"
              >
                <span>Se alle priser</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 8. OFTE STILLEDE SPØRGSMÅL (FAQ) */}
      <section className="py-16 sm:py-20 bg-white border-b border-stone-200/70">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Ofte stillede spørgsmål om energimærkning
            </h2>
            <p className="text-stone-600 text-sm mt-2">
              Læs svar på de hyppigste spørgsmål om energimærkning, gyldighed, regler og priser.
            </p>
          </div>

          <div className="space-y-3">
            {faqs.map((item, idx) => (
              <div
                key={idx}
                className="bg-[#FAF9F6] rounded-2xl border border-stone-200/90 overflow-hidden shadow-2xs"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-6 py-4 text-left font-bold text-stone-900 flex items-center justify-between gap-4 hover:bg-stone-100/60 transition-colors"
                >
                  <span className="text-sm sm:text-base">{item.q}</span>
                  <i
                    className={`fa-solid fa-chevron-down text-xs text-stone-400 transition-transform duration-200 shrink-0 ${
                      openFaq === idx ? "rotate-180 text-[#165B4C]" : ""
                    }`}
                    aria-hidden="true"
                  ></i>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5 pt-1 text-xs sm:text-sm text-stone-600 leading-relaxed border-t border-stone-200/60 whitespace-pre-line">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. SOCIAL PROOF */}
      <ReviewsSlider />

      {/* 10. KONTAKT FORMULAR */}
      <section id="kontakt" className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5 space-y-5">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
                Kontakt os for professionel rådgivning
              </h2>
              <div className="space-y-3 text-stone-600 text-sm leading-relaxed">
                <p>
                  Er du klar til at forbedre dit hjem eller energimærkningen på din bygning? Kontakt os i dag og få en professionel vurdering af din ejendoms energiforbrug.
                </p>
                <p>
                  Ring til os på telefonnummer <a href="tel:+4542499595" className="font-bold text-stone-900 whitespace-nowrap">42 49 95 95</a> , eller send en e-mail til <a href="mailto:info@etdanmark.com" className="font-bold text-stone-900 whitespace-nowrap">info@etdanmark.com</a> . Lad os hjælpe dig med at gøre din ejendom mere energieffektiv og værdifuld.
                </p>
                <p className="text-xs text-stone-500 italic pt-2">
                  Yderligere kan vi også tilbyde <Link to="/energiramme" className="text-emerald-700 underline font-medium">energirammeberegninger</Link> og <Link to="/trykproevning" className="text-emerald-700 underline font-medium">trykprøvning</Link> .
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-stone-50 border border-stone-200 space-y-2 text-xs text-stone-700">
                <p className="font-semibold text-stone-900">ET Danmark ApS</p>
                <p>Bjerrevej 62 A, 8700 Horsens</p>
                <p>Telefon: 42 49 95 95 &bull; CVR: 41026138</p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ContactForm
                defaultService="Energimærkning"
                title="Bestil energimærkning eller få et tilbud"
                subtitle="Du kan let og hurtigt sende os en besked i nedenstående formular."
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
